import formBuilder from "../../api/formBuilder";
import { capitalizeFirstLetter } from "@/common/functions";

const getDefaultState = () => {
  return {
    formName: "",
    templates: [],
    frequentFields: [],
    propertySortOrder: [],
    customFields: [],
    formStyle: {},
    legalNote: {},
    editPopUpForm: "",
    contactProperties: {},
    formOnlyProperties: {},
    contactList: [],
    userList: [],
    listDetails: {},
    userEmails: [],
    captchaEnabled: 0,
    formType: 0,
    status: 0,
    optionsData: {},
    isFreePlan: 0,
    formUrl: "",
    nextDisable: false,
    tags: [],
  };
};

// initial state
const state = getDefaultState();

// getters
const getters = {};

// actions
const actions = {
  createForm(_, params) {
    return formBuilder.createForm(params);
  },
  updateForm(_, { params, id }) {
    return formBuilder.updateForm(params, id);
  },
  getForm({ commit }, id) {
    return new Promise(function (resolve) {
      if (typeof id === "object") {
        id = id.id;
      } else {
        commit("resetState");
      }

      formBuilder.getForm(id, (data) => {
        if (data.data) {
          commit("setFormName", data.data.name);
          commit("setCaptchaEnabled", data.data.captchaEnabled);
          commit("setFormType", data.data.type);
          commit("setStatus", data.data.status);
          commit("setIsFreePlan", data.data.isFreePlan);
          commit("setFormUrl", data.data.form_url);
          const label = capitalizeFirstLetter(data.data.listName);
          commit("setListDetails", {
            value: data.data.listId ? data.data.listId : "",
            label,
          });
          if (data.data.propertySortOrder) {
            commit("setPropertySortOrder", data.data.propertySortOrder);
          }
          if (data.data.contactProperties) {
            commit("setContactProperties", data.data.contactProperties);
          }
          if (data.data.formOnlyProperties) {
            commit("setFormOnlyProperties", data.data.formOnlyProperties);
          }
          if (data.data.formStyle) {
            commit("setFormStyle", { value: data.data.formStyle });
          }
          if (data.data.legalNote) {
            commit("setLegalNote", { value: JSON.parse(data.data.legalNote) });
          }
          if (data.data.usersToNotify) {
            commit("setUserEmail", data.data.usersToNotify.split(","));
          }
          commit("setOptionsData", {
            isFollow: data.data.isFollow ? data.data.isFollow : 0,
            allowDuplicate: data.data.allowDuplicate
              ? data.data.allowDuplicate
              : 0,
            duplicateMessage: data.data.duplicateMessage
              ? data.data.duplicateMessage
              : "Email already exists",
            redirectionData: data.data.redirectionData
              ? data.data.redirectionData
              : "",
            redirectionType: data.data.redirectionType
              ? data.data.redirectionType
              : 1,
            tagIds: data.data.tags ? data.data.tags : [],
          });
        }
        resolve(data.status);
      });
    });
  },
  getTemplates({ commit }) {
    return new Promise(function (resolve) {
      formBuilder.getTemplates((data) => {
        commit("setTemplates", data.data.template_list);
        resolve(data.status);
      });
    });
  },
  getFrequentFields({ commit }, params) {
    return new Promise(function () {
      formBuilder.getFrequentFields(params, (data) => {
        commit("setFrequentFields", data.data);
      });
    });
  },
  getCustomFields({ commit }, params) {
    formBuilder.getCustomFields(params, (data) => {
      commit("setCustomFields", data.data);
    });
  },
  saveCustomField(_, params) {
    return formBuilder.saveCustomField(params);
  },
  publishForm(_, params) {
    return formBuilder.publishForm(params.id);
  },
  saveFormOnlyProperty(_, { params, formId }) {
    return formBuilder.saveFormOnlyProperty(params, formId);
  },
  updateFormProperty(_, { params, formId, propId }) {
    return formBuilder.updateFormProperty(params, formId, propId);
  },
  updateFormOnlyProperty(_, { params, formId, propId }) {
    return formBuilder.updateFormOnlyProperty(params, formId, propId);
  },
  addFormProperty(_, { params, formId, propId }) {
    return formBuilder.addFormProperty(params, formId, propId);
  },
  deleteFormProperty(_, { formId, formPropId, formOnlyId }) {
    if (formPropId) {
      return formBuilder.deleteFormProperty(formId, formPropId);
    }
    return formBuilder.deleteFormOnlyProperty(formId, formOnlyId);
  },
  viewFormProperty(_, params) {
    const formId = params.form_id;
    const propId = params.prop_id;
    if (formId) {
      delete params.form_id;
    }
    if (propId) {
      delete params.prop_id;
    }
    return formBuilder.viewFormProperty(formId, propId);
  },
  saveTemplateProperty(_, { formId, tempId }) {
    return formBuilder.saveTemplateProperty(formId, tempId);
  },
  searchList({ commit }, params) {
    formBuilder.searchList(params, (data) => {
      commit("setContactList", data.data);
    });
  },
  searchUsers({ commit }, params) {
    formBuilder.searchUsers(params, (data) => {
      commit("setUserList", data.data);
    });
  },
  shareForm(_, id) {
    return formBuilder.shareForm(id);
  },
};

// mutations
const mutations = {
  setFormName(state, data) {
    state.formName = data;
  },
  setTemplates(state, data) {
    state.templates = data;
  },
  setFrequentFields(state, data) {
    state.frequentFields = data;
  },
  setCustomFields(state, data) {
    const filteredData = data.filter(
      (item) => item.field_value !== "mailbox_provider"
    );
    state.customFields = filteredData;
  },
  setFormStyle(state, { value, key }) {
    if (key) {
      const style = {};
      style[key] = value;
      state.formStyle = { ...state.formStyle, ...style };
    } else {
      state.formStyle = value;
    }
  },
  setLegalNote(state, { value, key }) {
    if (key) {
      const legalNote = {};
      legalNote[key] = value;
      state.legalNote = { ...state.legalNote, ...legalNote };
    } else {
      state.legalNote = value;
    }
  },
  setPropertySortOrder(state, propertySortOrder) {
    state.propertySortOrder = propertySortOrder;
  },
  updatePropertySortOrder(
    state,
    {
      contactId,
      rowIndex = -1,
      colIndex = -1,
      formOnlyId,
      oldRowIndex = -1,
      oldColIndex = -1,
    }
  ) {
    let data = {};
    if (rowIndex !== -1 && oldRowIndex === -1 && oldColIndex === -1) {
      if (contactId) {
        data.contact_id = contactId;
      }
      if (formOnlyId) {
        data.form_only_id = formOnlyId;
      }
    } else {
      data = { ...state.propertySortOrder[oldRowIndex].items[oldColIndex] };
      state.propertySortOrder[oldRowIndex].items.splice(oldColIndex, 1);
    }
    if (rowIndex !== -1) {
      if (colIndex === -1) {
        state.propertySortOrder.splice(rowIndex, 0, { items: [data] });
      } else {
        state.propertySortOrder[rowIndex].items.splice(colIndex, 0, data);
      }
    }
    if (oldRowIndex !== -1 && oldColIndex !== -1) {
      if (rowIndex !== -1 && colIndex === -1 && oldRowIndex > rowIndex) {
        oldRowIndex++;
      }
      if (!state.propertySortOrder[oldRowIndex].items.length) {
        state.propertySortOrder.splice(oldRowIndex, 1);
      }
    }
  },
  setEditPopUpForm(state, value) {
    state.editPopUpForm = value;
  },
  setContactProperties(state, contactProperties) {
    state.contactProperties = contactProperties;
  },
  updateContactProperties(state, { id, item }) {
    const props = {};
    props[id] = item;
    state.contactProperties = { ...state.contactProperties, ...props };
  },
  setFormOnlyProperties(state, formOnlyProperties) {
    state.formOnlyProperties = formOnlyProperties;
  },
  updateFormOnlyProperties(state, { id, item }) {
    const props = {};
    props[id] = item;
    state.formOnlyProperties = { ...state.formOnlyProperties, ...props };
  },
  setContactList(state, data) {
    state.contactList = data;
  },
  setUserList(state, data) {
    state.userList = data;
  },
  setListDetails(state, data) {
    state.listDetails = data;
  },
  setUserEmail(state, data) {
    state.userEmails = data;
  },
  setCaptchaEnabled(state, captchaEnabled) {
    state.captchaEnabled = captchaEnabled;
  },
  setFormType(state, type) {
    state.formType = type;
  },
  setFormUrl(state, formUrl) {
    state.formUrl = formUrl;
  },
  setStatus(state, status) {
    state.status = status;
  },
  setIsFreePlan(state, isFreePlan) {
    state.isFreePlan = isFreePlan;
  },
  setOptionsData(state, data) {
    state.optionsData = data;
  },
  setNextDisable(state, data) {
    state.nextDisable = data;
  },
  resetState(state) {
    Object.assign(state, getDefaultState());
  },
};
export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
